import type { Force } from './atomizerSamples'

export type ShelfRule = { force: Force; rule: string; trigger: string; source: string }

export type Shelf = {
  id: string
  name: string
  dot: string
  what: string
  count: string
  source: string
  /** Colour of the source code in the table. */
  sourceTone: string
  rules: ShelfRule[]
}

export const LIBRARY_ALL = '2,923'

export const SHELVES: Shelf[] = [
  {
    id: 'regulatory',
    name: 'Regulatory',
    dot: '#1a1917',
    what: 'FDA 21 CFR 202 · OPDP guidance · HIPAA Privacy Rule · FTC endorsements',
    count: '1,140',
    source: 'REG',
    sourceTone: '#1a1917',
    rules: [
      { force: 'NEVER', rule: 'include PHI in an outbound draft', trigger: 'patient identifier', source: 'HIPAA §164.502' },
      { force: 'MUST', rule: 'quote dosing from the approved label', trigger: 'dosing', source: '21 CFR 202.1' },
      { force: 'MUST', rule: 'present risk alongside benefit', trigger: 'efficacy claim', source: 'OPDP guidance II.A' },
      { force: 'NEVER', rule: 'state or imply off-label use', trigger: 'indication', source: '21 CFR 202.1(e)' },
    ],
  },
  {
    id: 'industry',
    name: 'Industry',
    dot: '#6b6862',
    what: 'PhRMA Code · AMA gift guidance',
    count: '212',
    source: 'REG',
    sourceTone: '#1a1917',
    rules: [
      { force: 'NEVER', rule: 'offer items of value to an HCP beyond educational materials', trigger: 'HCP interaction', source: 'PhRMA Code §2' },
      { force: 'MUST', rule: 'disclose speaker fees where required', trigger: 'speaker program', source: 'PhRMA Code §7' },
      { force: 'SHOULD', rule: 'route meal requests through compliance', trigger: 'HCP meal', source: 'PhRMA Code §2' },
    ],
  },
  {
    id: 'brand',
    name: 'Brand',
    dot: '#e8590c',
    what: 'voice · claims language · approved terminology',
    count: '830',
    source: 'POL',
    sourceTone: '#1a1917',
    rules: [
      { force: 'MUST', rule: 'use the approved product name and capitalization', trigger: 'product mention', source: 'Brand book §1.2' },
      { force: 'NEVER', rule: 'use superlatives about efficacy', trigger: 'efficacy claim', source: 'Claims guide §4' },
      { force: 'MUST', rule: 'write in med-info voice for HCP replies', trigger: 'HCP audience', source: 'Voice guide §2' },
    ],
  },
  {
    id: 'roles',
    name: 'Roles',
    dot: '#2f6fed',
    what: 'med-info · MSL · field rep · reviewer',
    count: '227',
    source: 'POL',
    sourceTone: '#1a1917',
    rules: [
      { force: 'NEVER', rule: 'let a field rep answer an off-label question', trigger: 'off-label inquiry', source: 'Role: field rep' },
      { force: 'MUST', rule: 'route unsolicited medical requests to med-info', trigger: 'medical inquiry', source: 'Role: field rep' },
      { force: 'MUST', rule: 'attach citations on every MSL response', trigger: 'MSL response', source: 'Role: MSL' },
    ],
  },
  {
    id: 'house',
    name: 'House',
    dot: '#b45309',
    what: 'corrections and meeting notes that became rules',
    count: '418',
    source: 'MTG · FIX',
    sourceTone: '#b45309',
    rules: [
      { force: 'MUST', rule: 'never skip review on a “quick” reply', trigger: 'request marked quick', source: 'D. Okafor · Sep 3' },
      { force: 'MUST', rule: 'two paragraphs, citations attached, for HCP replies', trigger: 'HCP reply', source: 'MLR sync · Aug 21' },
      { force: 'NEVER', rule: 'send figures before the custodian posts', trigger: 'performance figure', source: 'M. Rao · Sep 2' },
    ],
  },
  {
    id: 'runbooks',
    name: 'Agent runbooks',
    dot: '#0f766e',
    what: 'what each step may and may not do',
    count: '96',
    source: 'POL',
    sourceTone: '#1a1917',
    rules: [
      { force: 'MUST', rule: 'hold denials for clinician sign-off', trigger: 'adverse determination', source: 'prior-auth runbook, step 4' },
      { force: 'MUST', rule: 'verify any bank change by callback before release', trigger: 'vendor bank change', source: 'AP runbook, step 2' },
      { force: 'NEVER', rule: 'auto-close on CPT 72148', trigger: 'CPT 72148', source: 'UM committee · Sep 4' },
    ],
  },
]

export const USE_STEPS = [
  { t: 'what you asked', b: 'The prompt, exactly as typed. Synthia never rewrites it.' },
  { t: 'what applies this time', b: 'The situational rules for this request: the audience, the workflow, the house style.' },
  { t: 'what always applies', b: 'The always-on rules bound to your deployment: privacy, regulatory, safety. They go in no matter what the prompt says.' },
  { t: 'what the model actually receives', b: 'One assembled request, with the rules that can’t be skipped at the front.' },
  { t: 'the check', b: 'Before the call goes out: are the required rules in? If yes, it runs. If no, it stops and the failure is logged.' },
  { t: 'the proof', b: 'A signed receipt: which rules went in, which versions, what triggered them, when.' },
]
